import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip, LabelList,
} from "recharts";
import { MOVES } from "../data/moves";
import {
  ShieldCheck, LogOut, Trophy, Flame, BookOpen, Video, Target,
  Loader2, Users, TrendingUp, Medal, CheckCircle2, Circle, FileBarChart, X, Printer, Grid3x3,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const moveName = (id) => MOVES.find((m) => m.id === id)?.name || id;

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-[#141414] border border-white/10 rounded-xl p-4 sm:p-5">
    <div className="flex items-center gap-2 mb-2">
      <Icon size={15} className="text-[#C41E24]" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">{label}</span>
    </div>
    <div className="font-cover text-4xl sm:text-5xl text-white leading-none">{value}</div>
  </div>
);

const MiniRing = ({ done, total }) => {
  const pct = total ? (done / total) * 100 : 0;
  const color = pct >= 100 ? "#22C55E" : pct > 0 ? "#D4AF37" : "#4b4b4b";
  const r = 16, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <div className="relative w-11 h-11 shrink-0">
      <svg width="44" height="44" className="-rotate-90">
        <circle cx="22" cy="22" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
        <circle cx="22" cy="22" r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] font-bold" style={{ color }}>{done}/{total}</span>
    </div>
  );
};

// Reusable dashboard body — shared by the club-coach view and the Head Coach preview.
export function ClubDashboardBody({ data, onSelectTeam, onPlayerClick }) {
  const [showReport, setShowReport] = useState(false);
  const s = data.summary || {};
  const drills = (data.weekly_report || []).map((w) => w.drill_id);
  const weekly = (data.weekly_report || []).map((w) => ({
    name: moveName(w.drill_id),
    completed: w.completed,
    remaining: Math.max(0, (w.team_size || 0) - w.completed),
    pct: w.team_size ? Math.round((w.completed / w.team_size) * 100) : 0,
  }));
  const board = data.leaderboard || [];
  const engagement = board.slice(0, 8).map((r) => ({ name: r.name, points: r.points, rank: r.rank }));
  const hasWeekly = weekly.length > 0;
  const teams = data.teams || [];
  const lockedTeam = data.locked_team || null;
  const selTeam = data.selected_team || "";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-10">
      {/* Team selector + report generator */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {lockedTeam ? (
          <div className="flex items-center gap-2.5" data-testid="locked-team-badge">
            <Users size={16} className="text-white/40" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-white/50">Team</span>
            <span className="text-sm font-bold text-white bg-[#C41E24]/15 border border-[#C41E24]/40 rounded-md px-3 py-1.5">{lockedTeam}</span>
          </div>
        ) : teams.length > 0 ? (
          <div className="flex items-center gap-2.5">
            <Users size={16} className="text-white/40" />
            <label htmlFor="team-select" className="text-[11px] font-bold uppercase tracking-widest text-white/50">Team</label>
            <select id="team-select" data-testid="team-select" value={selTeam}
              onChange={(e) => onSelectTeam(e.target.value)}
              className="bg-[#141414] border border-white/15 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24] min-w-[180px]">
              <option value="">{`All Teams (${teams.length})`}</option>
              {teams.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        ) : <div />}
        <button data-testid="generate-report-btn" onClick={() => setShowReport(true)} disabled={board.length === 0}
          className="flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-md transition-colors disabled:opacity-40">
          <FileBarChart size={15} /> Generate Weekly Summary
        </button>
      </div>

      {board.length === 0 ? (
        <div data-testid="club-empty" className="bg-[#141414] border border-white/10 rounded-2xl p-10 text-center">
          <Users size={34} className="text-white/25 mx-auto mb-4" />
          <h2 className="font-headings text-2xl uppercase tracking-wide mb-2">No players yet</h2>
          <p className="text-sm text-white/45 max-w-md mx-auto">No players found for <b className="text-white/70">{selTeam || data.club_name}</b>. Once players set their club to <b className="text-white/70">{data.club_name}</b> in their profile, they'll appear here in real time.</p>
        </div>
      ) : (
      <>
      <section>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} className="text-[#C41E24]" />
          <h2 className="font-headings text-2xl uppercase tracking-wide">Team Engagement</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <StatCard testid="stat-team-size" icon={Users} label="Players" value={data.team_size} />
          <StatCard testid="stat-participation" icon={Target} label="Participation" value={`${s.participation_rate || 0}%`} />
          <StatCard testid="stat-chapters" icon={BookOpen} label="Chapters Done" value={s.total_chapters_completed || 0} />
          <StatCard testid="stat-proofs" icon={Video} label="Video Proofs" value={s.total_video_proofs || 0} />
          <StatCard testid="stat-streaks" icon={Flame} label="Active Streaks" value={s.active_streaks || 0} />
        </div>
      </section>

      <section data-testid="engagement-grid">
        <div className="flex items-center gap-2 mb-1">
          <Grid3x3 size={18} className="text-[#C41E24]" />
          <h2 className="font-headings text-2xl uppercase tracking-wide">This Week's Reps</h2>
        </div>
        <p className="text-sm text-white/45 mb-5">Which players have completed each required drill this week{data.weekly_tip_title ? ` · ${data.weekly_tip_title}` : ""}.</p>
        {!hasWeekly ? (
          <p className="text-sm text-white/40 bg-[#141414] border border-white/10 rounded-2xl p-6 text-center">No weekly training session is set up yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {board.map((r) => {
              const doneIds = new Set(r.weekly_done_ids || []);
              const full = r.weekly_done === r.weekly_total && r.weekly_total > 0;
              const clickable = !!onPlayerClick && !!r.id;
              return (
                <div key={r.rank} data-testid={`engagement-card-${r.rank}`}
                  onClick={clickable ? () => onPlayerClick(r.id) : undefined}
                  role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (e) => { if (e.key === "Enter") onPlayerClick(r.id); } : undefined}
                  className={`rounded-xl border p-4 flex items-center gap-3 transition-colors ${full ? "border-[#22C55E]/50 bg-[#22C55E]/[0.06]" : "border-white/10 bg-[#141414]"} ${clickable ? "cursor-pointer hover:border-[#C41E24]" : ""}`}>
                  <MiniRing done={r.weekly_done} total={r.weekly_total} />
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold text-sm truncate ${clickable ? "hover:text-[#C41E24]" : ""}`}>{r.name}</div>
                    {r.team && <div className="text-[10px] text-white/40 uppercase tracking-wider truncate">{r.team}</div>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {drills.map((d) => {
                        const ok = doneIds.has(d);
                        return (
                          <span key={d} title={moveName(d)}
                            className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${ok ? "text-[#22C55E] bg-[#22C55E]/12" : "text-white/35 bg-white/5"}`}>
                            {ok ? <CheckCircle2 size={10} /> : <Circle size={10} />} {moveName(d).split(" ")[0]}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section data-testid="weekly-report">
        <div className="flex items-center gap-2 mb-1">
          <Target size={18} className="text-[#C41E24]" />
          <h2 className="font-headings text-2xl uppercase tracking-wide">Weekly Completion Report</h2>
        </div>
        <p className="text-sm text-white/45 mb-5">How many of your {data.team_size} player{data.team_size === 1 ? "" : "s"} finished each drill this week.</p>
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-4 sm:p-6">
          {!hasWeekly ? (
            <p className="text-sm text-white/40 py-6 text-center">No weekly training session is set up yet.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mb-6">
                <div>
                  <div className="font-cover text-5xl text-[#C41E24] leading-none">{s.weekly_completion_rate || 0}%</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">Completed all 3 drills</div>
                </div>
                <div>
                  <div className="font-cover text-5xl text-white leading-none">{s.all_complete_players || 0}<span className="text-2xl text-white/40">/{data.team_size}</span></div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">Players fully done</div>
                </div>
              </div>
              <div style={{ width: "100%", height: 260 }} data-testid="weekly-chart">
                <ResponsiveContainer>
                  <BarChart data={weekly} margin={{ top: 24, right: 8, left: -18, bottom: 0 }} barCategoryGap="30%">
                    <XAxis dataKey="name" tick={{ fill: "#ffffff88", fontSize: 12 }} tickLine={false} axisLine={{ stroke: "#ffffff20" }} />
                    <YAxis allowDecimals={false} domain={[0, data.team_size]} tick={{ fill: "#ffffff66", fontSize: 12 }} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#0d0d0d", border: "1px solid #ffffff20", borderRadius: 8, color: "#fff", fontSize: 12 }} formatter={(v, n) => [`${v} player${v === 1 ? "" : "s"}`, n === "completed" ? "Completed" : "Not yet"]} />
                    <Bar dataKey="completed" stackId="a" fill="#C41E24">
                      <LabelList dataKey="pct" position="top" formatter={(v) => `${v}%`} fill="#fff" fontSize={12} fontWeight={700} />
                    </Bar>
                    <Bar dataKey="remaining" stackId="a" radius={[4, 4, 0, 0]} fill="#2a2a2a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-5 mt-4 text-[11px] text-white/50">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#C41E24]" /> Completed</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#2a2a2a]" /> Not yet</span>
              </div>
            </>
          )}
        </div>
      </section>

      <section data-testid="engagement-chart-section">
        <div className="flex items-center gap-2 mb-5">
          <Medal size={18} className="text-[#C41E24]" />
          <h2 className="font-headings text-2xl uppercase tracking-wide">Points By Player</h2>
        </div>
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-4 sm:p-6" style={{ width: "100%", height: Math.max(180, engagement.length * 44 + 24) }}>
          <ResponsiveContainer>
            <BarChart layout="vertical" data={engagement} margin={{ top: 0, right: 40, left: 8, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={90} tick={{ fill: "#ffffffcc", fontSize: 12 }} tickLine={false} axisLine={false} />
              <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#0d0d0d", border: "1px solid #ffffff20", borderRadius: 8, color: "#fff", fontSize: 12 }} formatter={(v) => [`${v} pts`, "Points"]} />
              <Bar dataKey="points" radius={[0, 4, 4, 0]} barSize={20}>
                {engagement.map((e, i) => <Cell key={i} fill={e.rank === 1 ? "#F5C518" : "#C41E24"} />)}
                <LabelList dataKey="points" position="right" fill="#fff" fontSize={12} fontWeight={700} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section data-testid="team-leaderboard">
        <div className="flex items-center gap-2 mb-5">
          <Trophy size={18} className="text-[#C41E24]" />
          <h2 className="font-headings text-2xl uppercase tracking-wide">Team Leaderboard</h2>
        </div>
        <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden">
          {board.map((r) => {
            const clickable = !!onPlayerClick && !!r.id;
            const go = clickable ? () => onPlayerClick(r.id) : undefined;
            return (
            <div key={r.rank} data-testid={`team-row-${r.rank}`} className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 border-b border-white/5 last:border-0">
              <div className={`w-8 text-center font-cover text-2xl leading-none ${r.rank === 1 ? "text-[#F5C518]" : r.rank <= 3 ? "text-[#C41E24]" : "text-white/35"}`}>{r.rank}</div>
              {r.avatar_url ? (
                <img loading="lazy" decoding="async" src={`${BACKEND}${r.avatar_url}`} alt="" onClick={go}
                  data-testid={clickable ? `team-player-avatar-${r.id}` : undefined}
                  className={`w-10 h-10 rounded-full object-cover flex-shrink-0 border border-white/10 ${clickable ? "cursor-pointer hover:border-[#C41E24]" : ""}`} />
              ) : (
                <div onClick={go} data-testid={clickable ? `team-player-avatar-${r.id}` : undefined}
                  className={`w-10 h-10 rounded-full bg-[#C41E24]/15 text-[#C41E24] font-bold flex items-center justify-center flex-shrink-0 ${clickable ? "cursor-pointer hover:bg-[#C41E24]/25" : ""}`}>{r.name[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate flex items-center gap-2">
                  {clickable
                    ? <button type="button" data-testid={`team-player-link-${r.id}`} onClick={go} className="truncate hover:text-[#C41E24] transition-colors text-left">{r.name}</button>
                    : r.name}
                  {r.team && <span className="text-[9px] text-white/40 uppercase tracking-wider">{r.team}</span>}
                  {r.badge > 0 && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: r.badge >= 30 ? "#F5C518" : "#C0C0C0", background: "rgba(255,255,255,0.06)" }}>{r.badge}-Day</span>}
                </div>
                <div className="text-[11px] text-white/45 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                  <span>{r.chapters} ch</span>
                  <span>{r.proofs} vids</span>
                  <span>{r.journals} logs</span>
                  <span className={`flex items-center gap-1 ${r.weekly_done === r.weekly_total && r.weekly_total > 0 ? "text-[#22C55E]" : "text-white/45"}`}>
                    <Target size={11} /> {r.weekly_done}/{r.weekly_total} weekly
                  </span>
                  {r.streak > 0 && <span className="flex items-center gap-1 text-[#C41E24]"><Flame size={11} /> {r.streak}d</span>}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-cover text-2xl text-[#C41E24] leading-none">{r.points}</div>
                <div className="text-[9px] uppercase tracking-wider text-white/40">pts</div>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      <p className="text-center text-[11px] text-white/30 pt-2">Read-only view · Next O Soccer · Creating Opportunities With Elite Training</p>
      </>
      )}

      {showReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="report-modal" onClick={() => setShowReport(false)}>
          <div className="bg-[#111] border border-white/15 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-6 border-b border-white/10">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C41E24]">Weekly Team Summary</div>
                <h3 className="font-headings text-2xl uppercase tracking-wide leading-tight mt-1">{selTeam || data.club_name}</h3>
                <p className="text-[11px] text-white/40 mt-1">{new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
              </div>
              <button data-testid="report-close" onClick={() => setShowReport(false)} className="text-white/50 hover:text-white p-1"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="text-center py-2">
                <div data-testid="report-participation" className="font-cover text-7xl text-[#C41E24] leading-none">{s.participation_rate || 0}%</div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-2">Total Participation</div>
                <div className="text-[11px] text-white/35 mt-1">{s.reps_done || 0} of {s.reps_possible || 0} required reps completed</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#0d0d0d] border border-white/10 rounded-lg p-4 text-center">
                  <div className="font-cover text-3xl text-[#22C55E]">{s.all_complete_players || 0}<span className="text-lg text-white/40">/{data.team_size}</span></div>
                  <div className="text-[10px] uppercase tracking-wider text-white/45 mt-1">Fully completed</div>
                </div>
                <div className="bg-[#0d0d0d] border border-white/10 rounded-lg p-4 text-center">
                  <div className="font-cover text-3xl text-white">{s.active_streaks || 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/45 mt-1">Active streaks</div>
                </div>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-white/50 mb-2">Drill breakdown</div>
                <div className="space-y-2">
                  {weekly.map((w) => (
                    <div key={w.name}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-white/80">{w.name}</span>
                        <span className="font-bold text-white">{w.completed}/{data.team_size} ({w.pct}%)</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-[#C41E24]" style={{ width: `${w.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => window.print()} data-testid="report-print" className="w-full flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white/80 hover:text-white text-xs font-bold uppercase tracking-wider py-3 rounded-md transition-colors">
                <Printer size={15} /> Print / Save as PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClubDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("clubCoachToken");

  const logout = useCallback(() => {
    localStorage.removeItem("clubCoachToken");
    localStorage.removeItem("clubCoachInfo");
    navigate("/club-login", { replace: true });
  }, [navigate]);

  const load = useCallback((teamFilter) => {
    const q = teamFilter ? `?team=${encodeURIComponent(teamFilter)}` : "";
    axios.get(`${API}/club-coach/dashboard${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (err?.response?.status === 401) { logout(); return; }
        toast.error("Couldn't load your team dashboard.");
      })
      .finally(() => setLoading(false));
  }, [token, logout]);

  useEffect(() => {
    if (!token) { navigate("/club-login", { replace: true }); return; }
    load("");
    // eslint-disable-next-line
  }, []);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white/50">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading team dashboard…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden" data-testid="club-dashboard">
      <div className="border-b border-white/10 bg-[#0d0d0d]/80 backdrop-blur sticky top-0 z-20 safe-top">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#C41E24]/15 border border-[#C41E24]/40 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={20} className="text-[#C41E24]" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C41E24]">Club Accountability</div>
              <h1 data-testid="club-name" className="font-headings text-xl sm:text-2xl uppercase tracking-wide leading-none truncate">{data.club_name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="hidden sm:block text-xs text-white/45 text-right">{data.coach_name}</span>
            <button data-testid="club-logout-btn" onClick={logout} className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs font-bold uppercase tracking-wider px-3 py-2 border border-white/10 rounded-md hover:border-white/30 transition-colors">
              <LogOut size={14} /> <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </div>
      <ClubDashboardBody data={data} onSelectTeam={(t) => { setLoading(true); load(t); }} />
    </div>
  );
}
